import {Button, Link} from "@heroui/react";
import {motion} from "framer-motion";
import ContactUsForm from "../../components/ContactUsForm.jsx";

/* Support channels shown in the left branding rail */
const CHANNELS = [
    {
        icon: "hgi-mail-01",
        title: "Email support",
        desc: "We typically reply within 24 hours.",
    },
    {
        icon: "hgi-comment-01",
        title: "Help center",
        desc: "Browse guides and common answers.",
    },
    {
        icon: "hgi-flash",
        title: "Priority for paid plans",
        desc: "Pro members jump to the front of the queue.",
    },
];

const STATS = [
    {value: "< 24h", label: "Avg. response"},
    {value: "98%", label: "Resolved"},
    {value: "24/7", label: "Always open"},
];

const ContactUsPage = () => {
    const email = localStorage.getItem('email');

    return (
        <div className="relative min-h-screen w-full overflow-hidden bg-slate-50">
            {/* ambient backdrop */}
            <span aria-hidden className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-violet-300/30 blur-3xl"/>
            <span aria-hidden className="pointer-events-none absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-fuchsia-300/20 blur-3xl"/>

            {/* top bar */}
            <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
                <Link href="/app" className="flex items-center gap-2">
                    <img src="/images/logo-full.png" alt="AudioDub" className="h-8 w-auto"/>
                </Link>
                <Button
                    as={Link} href="/app"
                    size="sm" radius="full" variant="flat"
                    className="font-sans"
                    startContent={<i className="hgi-stroke hgi-arrow-left-01 text-base"/>}
                >
                    Back to app
                </Button>
            </header>

            <main className="relative z-10 mx-auto max-w-6xl px-6 pb-16 pt-4">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* ── Left branding rail ── */}
                    <motion.aside
                        initial={{opacity: 0, x: -16}}
                        animate={{opacity: 1, x: 0}}
                        transition={{type: "spring", stiffness: 220, damping: 26}}
                        className="lg:col-span-5"
                    >
                        <div className="relative h-full overflow-hidden rounded-30 bg-gradient-to-br from-[#5A0AE0] via-[#7B2CE2] to-[#8C2CE2] p-8 text-white lg:sticky lg:top-6">
                            {/* aurora blobs */}
                            <span aria-hidden className="pointer-events-none absolute -top-20 -left-10 h-56 w-56 rounded-full bg-fuchsia-400/30 blur-3xl"/>
                            <span aria-hidden className="pointer-events-none absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-indigo-300/25 blur-3xl"/>
                            <span aria-hidden className="pointer-events-none absolute inset-0 bg-[url('./assets/images/bg-image-1.jpeg')] bg-cover bg-center opacity-10"/>

                            <div className="relative">
                                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-tiny font-medium ring-1 ring-white/25">
                                    <span className="relative flex h-2 w-2">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75"/>
                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300"/>
                                    </span>
                                    Support is online
                                </span>

                                <h1 className="mt-5 font-sans text-3xl font-semibold leading-tight tracking-tight">
                                    Let's get you<br/>back to creating.
                                </h1>
                                <p className="mt-3 max-w-sm text-white/80">
                                    Hit a snag with dubbing, voice cloning, or billing? Send us a note and a real human will get back to you.
                                </p>

                                {/* stat strip */}
                                <div className="mt-7 grid grid-cols-3 gap-3">
                                    {STATS.map((s) => (
                                        <div key={s.label} className="rounded-2xl bg-white/10 p-3 text-center ring-1 ring-white/15">
                                            <p className="font-sans text-xl font-semibold">{s.value}</p>
                                            <p className="mt-0.5 text-[11px] text-white/70">{s.label}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* channels */}
                                <div className="mt-7 space-y-3">
                                    {CHANNELS.map((c, i) => (
                                        <motion.div
                                            key={c.title}
                                            initial={{opacity: 0, y: 10}}
                                            animate={{opacity: 1, y: 0}}
                                            transition={{delay: 0.15 + i * 0.08, type: "spring", stiffness: 260, damping: 24}}
                                            className="flex items-start gap-3 rounded-2xl bg-white/10 p-3.5 ring-1 ring-white/15 transition-colors hover:bg-white/15"
                                        >
                                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/15 ring-1 ring-white/25">
                                                <i className={`hgi-stroke ${c.icon} text-lg`}/>
                                            </span>
                                            <div className="leading-tight">
                                                <p className="font-medium">{c.title}</p>
                                                <p className="text-sm text-white/70">{c.desc}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.aside>

                    {/* ── Right form card ── */}
                    <motion.section
                        initial={{opacity: 0, y: 16}}
                        animate={{opacity: 1, y: 0}}
                        transition={{type: "spring", stiffness: 220, damping: 26, delay: 0.08}}
                        className="lg:col-span-7"
                    >
                        <div className="rounded-30 border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(90,10,224,0.35)] sm:p-8">
                            <div className="flex items-center gap-3">
                                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-violet-500/15 to-fuchsia-500/15 ring-1 ring-violet-200/70">
                                    <i className="hgi-stroke hgi-mail-edit-01 text-xl text-primary"/>
                                </span>
                                <div>
                                    <h2 className="font-sans text-xl font-semibold text-slate-800">Send us a message</h2>
                                    <p className="text-sm text-slate-500">Fill in the details below — fields marked are required.</p>
                                </div>
                            </div>

                            <div className="mt-2">
                                <ContactUsForm email={email}/>
                            </div>
                        </div>
                    </motion.section>
                </div>
            </main>
        </div>
    );
};

export default ContactUsPage;
